import React from 'react';
import {
  AISummarizeMetadata,
  OutputLength,
  AIInferenceStreamUsage,
  AISummarizeKind,
  AIToolInvocation,
  AIAuthorDetectionInputDetection,
  AIAuthorRunbookInputDetection,
  AIAuthorSkillInputSkill,
  RiskScoreEvidence,
} from 'Generated/schema';
import storage from 'Helpers/storage';
import { PANTHER_AI_OUTPUT_LENGTH_STORAGE_KEY } from 'Source/constants';
import useFetchGenericAiSummaryV3 from './useFetchGenericAiSummaryV3';

export interface RerunFormValues {
  additionalPrompt: string;
  kind: AISummarizeKind;
  outputLength: OutputLength;
}

type GenericAISummaryContextProviderProps = {
  kind: AISummarizeKind;
  alias: string;
  children?: React.ReactElement;
  // TODO: refactor this to remove events and detection and pass metadata context instead
  events?: [Record<string, any>];
  detection?: AIAuthorDetectionInputDetection;
  runbookDetection?: AIAuthorRunbookInputDetection;
  skill?: AIAuthorSkillInputSkill;
  currentProfile?: string;
  metadata?: AISummarizeMetadata;
  syncStreamIdWithUrl?: boolean;
  lastModified?: string;
  onStreamComplete?: (summary: string, userPrompt: string, usage?: AIInferenceStreamUsage) => void;
  private?: boolean;
};

type GenericAISummaryContextValues = {
  rerun: (values: RerunFormValues, attachments?: string[]) => void;
  alias?: string;
  aiSummary: string;
  aiSummaryError: string;
  isLoading: boolean;
  cancelPrompt: () => void;
  clearStream: () => void;
  isCancelled: boolean;
  initialUserPrompt: string;
  kind: AISummarizeKind;
  usage: AIInferenceStreamUsage | null;
  checkingForCachedResponse: boolean;
  hasCachedResponse: boolean;
  toolInvocations: AIToolInvocation[];
  streamId: string;
  riskScoreEvidence: RiskScoreEvidence | null;
};

export const GenericAISummaryContextV3 = React.createContext(null);

const getStorageKeyForKind = (kind: AISummarizeKind) =>
  `${PANTHER_AI_OUTPUT_LENGTH_STORAGE_KEY}.${kind}`;

export const persistAiOutputLengthSetting = (outputLength: OutputLength, kind: AISummarizeKind) =>
  storage.local.write(getStorageKeyForKind(kind), outputLength);

export const getAiOutputLengthSetting = (kind: AISummarizeKind) =>
  storage.local.read<OutputLength>(getStorageKeyForKind(kind)) ??
  // fall back to global setting if kind-specific setting is not found (backward compatibility)
  storage.local.read<OutputLength>(PANTHER_AI_OUTPUT_LENGTH_STORAGE_KEY) ??
  OutputLength.Medium;

export function GenericAISummaryContextProviderV3({
  kind,
  alias,
  children,
  events,
  detection,
  runbookDetection,
  skill,
  currentProfile,
  metadata,
  syncStreamIdWithUrl = false,
  lastModified,
  onStreamComplete,
  private: isPrivate,
}: GenericAISummaryContextProviderProps) {
  const {
    rerun,
    aiSummary,
    aiSummaryError,
    isLoading,
    cancelPrompt,
    clearStream,
    initialUserPrompt,
    isCancelled,
    usage,
    checkingForCachedResponse,
    hasCachedResponse,
    toolInvocations,
    streamId,
    riskScoreEvidence,
  } = useFetchGenericAiSummaryV3({
    kind,
    alias,
    events,
    metadata,
    detection,
    runbookDetection,
    skill,
    currentProfile,
    syncStreamIdWithUrl,
    lastModified,
    onStreamComplete,
    private: isPrivate,
  });

  const providerState = React.useMemo(
    () => ({
      rerun,
      alias,
      aiSummary,
      aiSummaryError,
      isLoading,
      cancelPrompt,
      clearStream,
      isCancelled,
      initialUserPrompt,
      kind,
      usage,
      checkingForCachedResponse,
      hasCachedResponse,
      toolInvocations,
      streamId,
      riskScoreEvidence,
    }),
    [
      rerun,
      alias,
      aiSummary,
      aiSummaryError,
      isLoading,
      cancelPrompt,
      clearStream,
      isCancelled,
      initialUserPrompt,
      kind,
      usage,
      checkingForCachedResponse,
      hasCachedResponse,
      toolInvocations,
      streamId,
      riskScoreEvidence,
    ],
  );

  return (
    <GenericAISummaryContextV3.Provider value={providerState}>
      {children}
    </GenericAISummaryContextV3.Provider>
  );
}

export function useGenericAISummaryContextV3() {
  const aiSummaryContext =
    React.useContext<GenericAISummaryContextValues>(GenericAISummaryContextV3);
  return aiSummaryContext;
}
